import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  NgForm,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Params, Router, RouterModule } from '@angular/router';
import { MatInputModule } from '@angular/material/input';
import { CommonModule } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject } from 'rxjs';
import { delay, takeUntil } from 'rxjs/operators';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { ROUTE_AUTH } from '../../../framework/constants/route.auth.constants';
import { NotificationsService } from '../../../services/notifications.service';
import {
  MAIN_USER_TYPES,
  USER_GENERAL_CONTRACTOR,
  USER_MANAGER,
} from '../../../framework/constants/user.constants';
import { fadeInCustom } from '../../../../assets/styles/animations';
import { CurrentUserService } from '../../../services/current-user.service';
import { SubscriptionService } from '../../../services/subscription.service';
import { ISubscriptionPlan } from '../../../framework/constants/subscription.constants';
import { RegistrationData, RegistrationForm } from '../auth.constants';
import { RegisterManagerFormComponent } from './register-manager-form/register-manager-form.component';
import { RegisterServiceProviderFormComponent } from './register-service-provider-form/register-service-provider-form.component';

/**
 * Check if password and re typed password does match.
 * https://stackoverflow.com/questions/67231873/angular-11-how-to-validate-confirmpassword-is-same-as-password-using-reactive-fo
 */
const passwordMatch: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const password = control.get('password');
  const password_confirmation = control.get('password_confirmation');
  return password?.value === password_confirmation?.value ? null : { notMatched: true };
};

@Component({
  selector: 'app-register',
  templateUrl: './register.component.html',
  styleUrls: ['./register.component.scss'],
  animations: [fadeInCustom(300)],
  standalone: true,
  providers: [NgForm],
  imports: [
    CommonModule,
    MatInputModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatButtonModule,
    RouterModule,
    MatTooltipModule,
    NgScrollbarModule,
    RegisterManagerFormComponent,
    RegisterServiceProviderFormComponent,
  ],
})
export class RegisterComponent implements OnInit, AfterViewInit, OnDestroy {
  registrationForm = new FormGroup<RegistrationForm>(
    {
      company_name: new FormControl('', [Validators.required, Validators.minLength(2)]),
      first_name: new FormControl('', [Validators.required, Validators.minLength(3)]),
      last_name: new FormControl('', [Validators.required, Validators.minLength(3)]),
      email: new FormControl('', [Validators.required, Validators.email]),
      phone: new FormControl('', [Validators.required, Validators.minLength(10)]),
      password: new FormControl('', [Validators.required, Validators.minLength(8)]),
      password_confirmation: new FormControl('', [Validators.required, Validators.minLength(8)]),
      type: new FormControl(USER_MANAGER, [Validators.required]), // hidden for manager
      // license: new FormControl('', Validators.required), // hidden for manager, will be added from code
    },
    { validators: passwordMatch },
  );
  protected readonly MAIN_USER_TYPES = MAIN_USER_TYPES;

  accountType: WritableSignal<MAIN_USER_TYPES> = signal(MAIN_USER_TYPES.MANGER);
  loading: WritableSignal<boolean> = signal(false);
  invitationToken: WritableSignal<string> = signal(null);
  selectedPlan: WritableSignal<'basic' | 'premium'> = signal(null);
  plans: ISubscriptionPlan[];

  // user can register only as manager if he has selected a plan or has an invitation token (from params)
  isOnlyManager: Signal<boolean> = computed(
    () => !!this.selectedPlan() || !!this.invitationToken(),
  );
  isDestroyed$ = new Subject();

  constructor(
    protected currentUserService: CurrentUserService,
    protected router: Router,
    private activatedRoute: ActivatedRoute,
    private notif: NotificationsService,
    private cdr: ChangeDetectorRef,
    private subscriptionService: SubscriptionService,
  ) {}

  ngOnInit() {
    this.registrationForm.valueChanges.pipe(takeUntil(this.isDestroyed$)).subscribe((value) => {
      if (this.registrationForm.hasError('notMatched')) {
        this.registrationForm.get('password_confirmation').setErrors({ notMatched: true });
      } else {
        this.registrationForm.get('password_confirmation').setErrors(null);
      }
    });

    this.subscriptionService.getAllPlans().subscribe((plans) => {
      this.plans = plans;
    });
  }

  ngAfterViewInit() {
    // delay is needed to avoid ExpressionChangedAfterItHasBeenCheckedError
    this.activatedRoute.queryParams
      .pipe(takeUntil(this.isDestroyed$), delay(20))
      .subscribe(this.parseQueryParams.bind(this));
  }

  private parseQueryParams(params: Params) {
    if (params?.invitation_token) {
      this.invitationToken.set(params.invitation_token);
      this.cdr.detectChanges();
    }
    if (params?.email) {
      this.registrationForm.get('email').setValue(params.email);
    }
    if (params?.first_name) {
      this.registrationForm.get('first_name').setValue(params.first_name);
    }
    if (params?.last_name) {
      this.registrationForm.get('last_name').setValue(params.last_name);
    }
    if (params?.selected_plan) {
      this.selectedPlan.set(params.selected_plan);
    }
  }
  async register() {
    this.loading.set(true);
    this.notif.showLoading();
    const registrationData: RegistrationData = { ...this.registrationForm.getRawValue() };

    if (this.invitationToken()) {
      registrationData.invitation_token = this.invitationToken();
    }

    if (this.selectedPlan()) {
      // send the selected plan's id to create a subscription on backend without navigating to stripe checkout
      const plan = this.plans.find((plan) => plan.name.toLowerCase().includes(this.selectedPlan()));
      if (plan) {
        registrationData.billing_plan_id = plan.id;
        // this is hardcoded for now, but we might want to add a way to select it in the future
        registrationData.subscription_recurring_interval = 'month';
      } else {
        console.warn('Plan not found', this.selectedPlan());
      }
    }

    try {
      await this.currentUserService.register(registrationData);
      this.notif.showSuccess('Registration completed.');

      await this.router.navigate(['auth', ROUTE_AUTH.THANK_YOU], {
        queryParams: { invitation: !!this.invitationToken() },
      });
    } catch (error) {
      const errorMessage = error.error[Object.keys(error.error)[0]];
      let isString = false;
      if (typeof errorMessage === 'string' || errorMessage instanceof String) {
        isString = true;
      }
      this.notif.showError(
        `An error occurred during register${isString ? ': ' + errorMessage : '.'} `,
      );
    } finally {
      this.loading.set(false);
    }
  }

  toggleAccountType(accountType: MAIN_USER_TYPES) {
    if (accountType === MAIN_USER_TYPES.MANGER) {
      this.registrationForm.get('type').setValue(USER_MANAGER);
      this.registrationForm.removeControl('license');
    } else if (accountType === MAIN_USER_TYPES.SERVICE_PROVIDER) {
      this.registrationForm.get('type').setValue(null);
      this.registrationForm.addControl('license', new FormControl('', [Validators.required]));
    } else if (accountType === MAIN_USER_TYPES.GENERAL_CONTRACTOR) {
      this.registrationForm.get('type').setValue(USER_GENERAL_CONTRACTOR);
      this.registrationForm.addControl('license', new FormControl('', [Validators.required]));
    }
    this.accountType.set(accountType);
    this.registrationForm.markAsUntouched();
  }

  ngOnDestroy() {
    this.isDestroyed$.next(true);
    this.isDestroyed$.complete();
  }
}
